/**
 * Typed API client. Attaches the Cognito ID token to every request and shares
 * domain types with the backend via @irrigation/core.
 */

import type { CalibrationPreview, FieldCapacityObservation, FlumeConnection, MoistureSensor, RachioConnection, RunRecord, ScheduleConfig, SoilSnapshot, Tenant, TenantHealth, WettingEvent, Zone } from '@irrigation/core';
import { getConfig } from './config.js';
import { getIdToken } from './auth.js';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
  }
}

/** Fired when a request finds the session expired, so the shell can sign out. */
export const AUTH_EXPIRED_EVENT = 'auth:expired';

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const token = await getIdToken();
  if (!token) {
    window.dispatchEvent(new Event(AUTH_EXPIRED_EVENT));
    throw new ApiError(401, 'Your session has expired. Please sign in again.');
  }

  const res = await fetch(`${getConfig().apiUrl}${path}`, {
    method,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (res.status === 401) window.dispatchEvent(new Event(AUTH_EXPIRED_EVENT));
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  const data = text ? JSON.parse(text) : undefined;
  if (!res.ok) {
    throw new ApiError(res.status, (data && data.error) || `Request failed (${res.status}).`);
  }
  return data as T;
}

/** A discovered Rachio zone offered during onboarding. */
export interface RachioZoneSummary {
  rachioZoneId: string;
  zoneNumber: number;
  name: string;
  enabled: boolean;
  precipRateInPerHr?: number;
  efficiency?: number;
  /** Attributes inferred server-side from the controller's configuration. */
  nozzleType?: Zone['nozzleType'];
  /** Raw controller nozzle name (may be "UNRECOGNIZED" for newer head types). */
  nozzleName?: string;
  soilType?: Zone['soilType'];
  plantType?: Zone['plantType'];
  sunExposure?: Zone['sunExposure'];
  slope?: Zone['slope'];
  rootDepthIn?: number;
  areaSqFt?: number;
}

/** A Rachio controller offered in the device picker (multi-device accounts). */
export interface RachioDeviceSummary {
  deviceId: string;
  name: string;
  model?: string;
  zoneCount: number;
}

/** A Flume meter offered in the device picker (multi-meter accounts). */
export interface FlumeDeviceSummary {
  id: string;
  locationId: number;
  label: string;
  connected: boolean;
  batteryLevel?: string;
}

/** The four secrets Flume's password grant needs. Sent once, never returned. */
export interface FlumeCredentialsInput {
  clientId: string;
  clientSecret: string;
  username: string;
  password: string;
}

/** A sensor as returned by the API, ingest key included. */
export type SensorSummary = MoistureSensor;

/** Live soil state joined onto zones by the API (display only). */
export interface ZoneSoil {
  deficitIn: number;
  projectedNextWateringDate?: string;
  /** ISO datetime this zone most recently got water (any source), if known. */
  lastWateredAt?: string;
  asOf: string;
}

export type ZoneWithSoil = Zone & { soil?: ZoneSoil };

/** On-demand calibration read: the verdict plus data to draw the graph. */
export interface CalibrationCheck {
  preview: CalibrationPreview;
  observations: FieldCapacityObservation[];
  events: WettingEvent[];
  /** The zone after the check persisted (or cleared) any suggestion. */
  zone: Zone;
}

export const api = {
  getMe: () => request<Tenant>('GET', '/me'),
  updateMe: (patch: Partial<Pick<Tenant, 'location'>>) => request<Tenant>('PUT', '/me', patch),

  connectRachio: (token: string, deviceId?: string) =>
    request<{
      connection: RachioConnection;
      zones: RachioZoneSummary[];
      devices: RachioDeviceSummary[];
    }>('POST', '/rachio/connect', { token, deviceId }),

  /** Zones on the connected device, discovered with the stored token. */
  listRachioZones: () => request<RachioZoneSummary[]>('GET', '/rachio/zones'),

  /** Immediately stop all watering on the controller. */
  stopWatering: () => request<{ stopped: boolean }>('POST', '/rachio/stop'),

  /**
   * Connect a Flume water meter. The four secrets go up once and are never
   * returned; the response carries only connection status and the meter list.
   */
  connectFlume: (creds: FlumeCredentialsInput, deviceId?: string) =>
    request<{ connection: FlumeConnection; devices: FlumeDeviceSummary[] }>('POST', '/flume/connect', {
      ...creds,
      deviceId,
    }),

  disconnectFlume: () => request<{ connection: FlumeConnection }>('DELETE', '/flume/connect'),

  listZones: () => request<ZoneWithSoil[]>('GET', '/zones'),
  createZone: (zone: Partial<Zone>) => request<Zone>('POST', '/zones', zone),
  updateZone: (id: string, zone: Partial<Zone>) => request<Zone>('PUT', `/zones/${id}`, zone),
  deleteZone: (id: string) => request<void>('DELETE', `/zones/${id}`),

  /**
   * Manually run one or more zones right now (real water). Rachio runs them
   * back to back in the order given, so send them sorted by zone number.
   *
   * There is no single-zone client any more: the backend route
   * `POST /zones/{id}/run` still exists, but every caller here goes through the
   * batch, which is one command and therefore cannot abort itself.
   */
  runZones: (runs: { zoneId: string; minutes: number }[]) => request<RunRecord[]>('POST', '/zones/run', { runs }),

  /** Adopt the Rachio controller's settings for a drifted zone. */
  applyZoneDrift: (id: string) => request<Zone>('POST', `/zones/${id}/drift/apply`),
  /** Keep our settings; don't re-flag until Rachio's values change again. */
  dismissZoneDrift: (id: string) => request<Zone>('POST', `/zones/${id}/drift/dismiss`),

  /** Accept a zone's measured flow as its new normal (re-baseline). */
  applyFlowAnomaly: (id: string) => request<Zone>('POST', `/zones/${id}/flow-anomaly/apply`),

  /** Silence a flow anomaly without changing the baseline. */
  dismissFlowAnomaly: (id: string) => request<Zone>('POST', `/zones/${id}/flow-anomaly/dismiss`),

  /** Adopt the suggested sensor calibration bounds for a zone. */
  applyCalibration: (id: string) => request<Zone>('POST', `/zones/${id}/calibration/apply`),
  /** Keep current bounds; the same suggestion won't re-flag. */
  dismissCalibration: (id: string) => request<Zone>('POST', `/zones/${id}/calibration/dismiss`),
  /** Compute where calibration stands right now (no waiting for the daily pass). */
  checkCalibration: (id: string) => request<CalibrationCheck>('POST', `/zones/${id}/calibration/check`),

  /** Start a calibration run: water briefly now, evaluate the plateau later.
   * Pass `minutes` to override the server-computed run duration for this run. */
  calibrateZone: (id: string, minutes?: number) =>
    request<Zone>('POST', `/zones/${id}/calibrate`, minutes !== undefined ? { minutes } : undefined),
  /** Clear a calibration-session marker (any status). */
  dismissCalibrationSession: (id: string) => request<Zone>('POST', `/zones/${id}/calibrate/dismiss`),
  /** Dismiss the precip-rate hint; the same ratio won't re-flag. */
  dismissPrecipHint: (id: string) => request<Zone>('POST', `/zones/${id}/precip-hint/dismiss`),
  /** Dismiss the sensor/model disagreement alert; the same streak won't re-flag. */
  dismissSensorDisagreement: (id: string) => request<Zone>('POST', `/zones/${id}/sensor-disagreement/dismiss`),

  /** Daily soil snapshots + hourly sensor readings for the history chart. */
  zoneHistory: (id: string, days = 30) =>
    request<{ snapshots: SoilSnapshot[]; readings: { at: string; moisturePct: number }[] }>(
      'GET',
      `/zones/${id}/history?days=${days}`,
    ),

  listSensors: () => request<SensorSummary[]>('GET', '/sensors'),
  createSensor: (name: string) => request<SensorSummary>('POST', '/sensors', { name }),
  deleteSensor: (id: string) => request<void>('DELETE', `/sensors/${id}`),

  getConfig: () => request<ScheduleConfig>('GET', '/config'),
  /** Partial update; pass `pausedUntil: null` to clear a pause. */
  updateConfig: (config: Partial<Omit<ScheduleConfig, 'pausedUntil'>> & { pausedUntil?: string | null }) =>
    request<ScheduleConfig>('PUT', '/config', config),

  /** Scheduler health for the signed-in tenant; null before the first planner pass. */
  getHealth: () => request<TenantHealth | null>('GET', '/health'),

  /** Runs for the given ISO dates (defaults server-side to today). */
  listRuns: (dates?: string[]) =>
    request<RunRecord[]>('GET', `/runs${dates && dates.length ? `?dates=${dates.join(',')}` : ''}`),

  /** Today's local sunrise ("HH:MM") for the tenant's location, if known. */
  getSunrise: () => request<{ date: string; sunriseLocal?: string }>('GET', '/sunrise'),
};
